import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';

import { AuthService } from '../../services/auth.service';
import { OnboardingService, WizardState } from '../../services/onboarding.service';

/**
 * vd-vendor-status-banner — bannière de BLOCAGE DE VENTE.
 *
 * Affichée tant que le compte vendeur n'est pas approuvé (statut EXPLICITE
 * incomplete / pending_review / rejected / suspended). Strictement alignée sur
 * le gate API ``require_vendor_approved`` via le champ ``can_sell`` : un compte
 * hérité (vendor_status NULL → can_sell=true) ne voit JAMAIS la bannière.
 *
 * 100% additif : si l'utilisateur n'est pas vendeur, ou si l'API échoue, ou si
 * la vente est autorisée, le composant ne rend rien.
 */
@Component({
  selector: 'vd-vendor-status-banner',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="vsb" *ngIf="show()" [attr.data-st]="state()?.vendor_status">
      <span class="material-icons-outlined ic">{{ icon() }}</span>
      <div class="txt">
        <strong>{{ title() }}</strong>
        <span>{{ message() }}</span>
      </div>
      <a class="cta" routerLink="/CompleteSignUp">{{ ctaLabel() }}</a>
    </div>
  `,
  styles: [`
    .vsb {
      display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-radius: 12px;
      margin-bottom: 16px; background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe;
    }
    .vsb[data-st="rejected"], .vsb[data-st="suspended"] { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
    .vsb .ic { font-size: 26px; }
    .vsb .txt { display: flex; flex-direction: column; line-height: 1.35; flex: 1; }
    .vsb .txt strong { font-size: 14.5px; }
    .vsb .txt span { font-size: 13px; opacity: .9; }
    .vsb .cta {
      text-decoration: none; font-weight: 700; font-size: 13px; padding: 9px 16px; border-radius: 8px;
      background: linear-gradient(135deg, #6366f1, #7c3aed); color: #fff; white-space: nowrap;
    }
  `],
})
export class VendorStatusBannerComponent implements OnInit {
  private readonly api = inject(OnboardingService);
  private readonly auth = inject(AuthService);

  readonly state = signal<WizardState | null>(null);
  readonly show = signal<boolean>(false);

  ngOnInit(): void {
    const roles = this.auth.getRolesFromToken();
    // Seuls les vendeurs/employés sont concernés par le gate de vente.
    if (!roles.includes('vendor') && !roles.includes('employee')) return;
    this.api.getState().subscribe({
      next: (st) => {
        this.state.set(st);
        this.show.set(!st.can_sell);
      },
      error: () => this.show.set(false),
    });
  }

  icon(): string {
    const s = this.state()?.vendor_status;
    if (s === 'pending_review') return 'hourglass_top';
    if (s === 'rejected') return 'report';
    if (s === 'suspended') return 'block';
    return 'storefront';
  }

  title(): string {
    const s = this.state()?.vendor_status;
    if (s === 'pending_review') return 'Compte en cours de validation';
    if (s === 'rejected') return 'Dossier à revoir';
    if (s === 'suspended') return 'Compte suspendu';
    return 'Finalisez votre inscription pour vendre';
  }

  message(): string {
    const s = this.state();
    if (s?.vendor_status === 'pending_review')
      return 'Notre équipe examine votre dossier. La publication sera débloquée après validation.';
    if (s?.vendor_status === 'rejected')
      return s.approval_note || 'Des informations sont nécessaires avant de pouvoir vendre.';
    if (s?.vendor_status === 'suspended')
      return s?.approval_note || 'Votre compte est temporairement suspendu. Contactez le support.';
    return 'Vous devez compléter et faire valider votre compte avant de publier vos produits.';
  }

  ctaLabel(): string {
    const s = this.state()?.vendor_status;
    if (s === 'pending_review') return 'Voir le suivi';
    return 'Compléter mon inscription';
  }
}
